import { inject, Injector } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { catchError, defaultIfEmpty, map, of, switchMap, zip } from 'rxjs';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { FleetValidation } from '../../../../common/interfaces/validation/fleet-validation';
import { ValidationService } from '../../../../common/services/validation.service';
import { FleetRoute } from '../enums/fleet-route';
import type { FleetForm } from '../helpers/fleet-form.helper';
import { FleetFormHelper } from '../helpers/fleet-form.helper';
import { FleetService } from '../services/fleet.service';
import type { SemiTrailerSize } from './../enums/semi-trailer-size';

export const editFleetResolver: ResolveFn<{
  validation: FleetValidation;
  form: FleetForm;
  hasAssignedFleet: boolean;
  assignedSemiTrailerSize: SemiTrailerSize | null;
}> = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const fleetService = inject(FleetService);
  const validationService = inject(ValidationService);
  const injector = inject(Injector);

  const uuid = route.paramMap.get('uuid')!;

  const redirect = () => {
    const urlTree = router.parseUrl(FleetRoute.FLEET_LIST);
    return new RedirectCommand(urlTree, { replaceUrl: true });
  };

  return fleetService.getFleet(uuid).pipe(
    switchMap((fleet) =>
      zip(
        of(fleet),
        validationService.getFleetValidation(fleet.generalInformation.category),
      ),
    ),
    map(([dto, validation]) => ({
      validation,
      form: FleetFormHelper.fromDto(dto),
      initial: FleetFormHelper.fromDto(dto),
      hasAssignedFleet: dto.vehicle != null || dto.trailer != null,
      assignedSemiTrailerSize:
        dto.trailer?.additionalParameters.semiTrailerSize ?? null,
    })),
    defaultIfEmpty(redirect()),
    catchError((response: OnyxHttpErrorResponse) => {
      ValidationHelper.handleUnexpectedResponse(response, injector, {
        keepOnNavigation: true,
      });
      return of(redirect());
    }),
  );
};
